import { BlitzPage } from "@blitzjs/next"
import Layout from "app/core/layouts/Layout"
import Footer from "components/footer"
import dynamic from "next/dynamic"
// Dynamic import to avoid SSR and hydration errors
const Header = dynamic(() => import("components/header"), { ssr: false })
import { STATIC_ASSETS_CLOUDFRONT_URL } from "app/constants"

const About: BlitzPage = () => {
  return (
    <div className="min-h-screen">
      <div
        style={{
          backgroundImage: `url(${STATIC_ASSETS_CLOUDFRONT_URL}/Parcel_Tree_Banner_DarkerGr_gdt.jpg)`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          imageRendering: "crisp-edges",
        }}
      >
        <div className="bg-white">
          <Header />
        </div>
        <div className="m-auto w-4/5 pb-20 pt-32">
          <div>
            <p className="pb-2 text-3xl font-medium text-white md:leading-snug lg:text-[40px] xl:text-[46px]">
              About Tree Equity Score
            </p>
            <p className="text-2xl font-light text-white md:leading-snug">
              We believe data belongs to everyone.
            </p>
          </div>
        </div>
      </div>

      <section
        className="bg-white"
        style={{
          backgroundImage: `url(${STATIC_ASSETS_CLOUDFRONT_URL}/Parcel_Tree_Outlines_noROW_lite_gradient.jpg)`,
          backgroundSize: "cover",
          backgroundPosition: "center center",
          backgroundRepeat: "no-repeat",
          imageRendering: "crisp-edges",
        }}
      >
        <div className="h-20"></div>
        <div className="m-auto w-4/5 border-2 border-brand-green-dark p-12 text-center text-[23px] font-medium uppercase leading-relaxed tracking-wide text-brand-green-dark xl:w-3/5 2xl:w-2/5">
          OUR GOAL IS THAT URBAN COMMUNITIES HAVE THE RESOURCES TO GET HEALTHY TREES TO THOSE WHO
          NEED THEM THE MOST.
        </div>
        <div className="h-28"></div>
        <div className="text-center">
          <p className="pb-2 font-bold uppercase tracking-wide text-brand-green-dark">
            Tree Equity Score
          </p>
          <p className="pb-10 text-2xl font-bold uppercase">By the numbers</p>
        </div>
        <div className="m-auto grid w-2/3 grid-cols-1 md:grid-cols-2 lg:w-1/2 xl:w-3/4 xl:grid-cols-4 2xl:w-2/3">
          <div className="mx-auto w-52">
            <img className="mb-8 inline h-10" src="/icons/neighborhood-ln.png" />
            <p className="inline text-5xl leading-loose text-brand-green-dark"> 200K</p>
            <p className="-mt-4 mb-4 text-lg leading-snug">
              Tree Equity Scores are available for nearly 200,000 urban neighborhoods in the United
              States.
            </p>
          </div>
          <div className="mx-auto w-52">
            <img className="mb-8 inline h-10" src="/icons/population-ln.png" />
            <p className="inline text-5xl leading-loose text-brand-green-dark"> 260M+</p>
            <p className="-mt-4 mb-4 text-lg leading-snug">
              More than 260 million people live in the neighborhoods covered by Tree Equity Score.
            </p>
          </div>
          <div className="mx-auto w-52">
            <img className="mb-8 inline h-10" src="/icons/plant-trees-ln.png" />
            <p className="inline text-5xl leading-loose text-brand-green-dark"> 500M</p>
            <p className="-mt-4 mb-4 text-lg leading-snug">
              We need over 500 million more new trees to achieve Tree Equity in all areas with
              inadequate tree cover.
            </p>
          </div>
          <div className="mx-auto w-52">
            <img className="mb-8 inline h-10" src="/icons/es-dollars-ln.png" />
            <p className="inline text-5xl leading-loose text-brand-green-dark"> 4.5B</p>
            <p className="-mt-4 mb-4 text-lg leading-snug">
              These trees would provide ecosystem service benefits valued at nearly $4.5 billion
              each year.
            </p>
          </div>
        </div>
        <div className="h-20 md:h-40"></div>
      </section>

      <section className="bg-white">
        <div className="m-auto grid w-11/12 grid-cols-1 md:grid-cols-2 xl:w-4/5 2xl:w-2/3">
          <div
            style={{
              backgroundImage: `url(${STATIC_ASSETS_CLOUDFRONT_URL}/Parcel_Tree_Banner_DarkerGr.jpg)`,
              backgroundSize: "cover",
              backgroundPosition: "bottom center",
              backgroundRepeat: "no-repeat",
              imageRendering: "crisp-edges",
            }}
            className="mx-5 mt-10 rounded-xl bg-brand-green-dark p-10 text-center text-xl font-light leading-tight text-white md:text-2xl lg:mx-10 2xl:p-16"
          >
            Planting trees in underserved neighborhoods addresses basic human rights to health,
            safety and welfare. The benefits to mitigate extreme heat, air pollution, public health
            and environmental hazards are well documented.
          </div>
          <div className="flex flex-col justify-center px-10 pt-20 text-gray-800 md:pt-0 lg:px-0">
            <p className="pb-5 text-2xl font-bold md:text-3xl">Join the movement</p>
            <p className="pb-6 text-lg">
              American Forests developed Tree Equity Score to address damaging environmental
              inequities in tree distribution common to cities and towns all across the U.S. Tree
              Equity Score establishes an equity-first standard to guide investment in critical
              urban tree infrastructure, starting with neighborhoods with the greatest need.
            </p>
            <p className="text-lg">
              We created Tree Equity Score so that every person has the information they need to
              advocate for the health and resilience of their community.
            </p>
          </div>
        </div>
      </section>

      <section>
        <div className="bg-gradient-to-b from-white to-[#ECF5F2]">
          <div className="h-36 lg:h-44"></div>
          <hr className="m-auto w-1/2 border-2 border-brand-green-dark"></hr>
          <p className="m-auto w-11/12 pt-8 text-center text-lg md:text-xl lg:w-1/2">
            American Forests partners with corporations, foundations, government agencies,
            community-based organizations, community leaders, nonprofit organizations and academic
            institutions at the local, state and federal levels to advance Tree Equity. Tree Equity
            Score was made possible by the generous financial support from partners.
          </p>
          <div className="h-20"></div>
        </div>
        <div className="bg-[#ECF5F2]">
          <div className="m-auto w-4/5 xl:w-2/3">
            <p className="pb-10 text-2xl font-bold uppercase">Collaborators</p>
            <div className="grid grid-cols-3 gap-10 md:grid-cols-4 lg:grid-cols-5">
              <img className="aspect-square" src="/itree.jpg" />
              <img className="aspect-square" src="/google.jpg" />
              <img className="aspect-square" src="/usfs.jpg" />
              <img className="aspect-square" src="/uvm.jpg" />
              <img className="aspect-square" src="/icp.jpg" />
            </div>
          </div>
          <div className="h-20"></div>

          <div className="m-auto w-4/5 xl:w-2/3">
            <p className="pb-10 text-2xl font-bold uppercase">Contributors</p>
            <div className="grid grid-cols-3 gap-5 sm:gap-10 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
              <img className="aspect-square" src="/ddcf.jpg" />
              <img className="aspect-square" src="/salesforce.jpg" />
              <img className="aspect-square" src="/microsoft.jpg" />
              <img className="aspect-square" src="/earthdefine.jpg" />
              <img className="aspect-square" src="/google.org.jpg" />
              <img className="aspect-square" src="/aws.jpg" />
              <img className="aspect-square" src="/jpb.jpg" />
              <img className="aspect-square" src="/weyerhaeuser.jpg" />
              <img className="aspect-square" src="/tazo.jpg" />
              <img className="aspect-square" src="/BOA.jpg" />
              <img className="aspect-square" src="/nfwf.jpg" />
              <img className="aspect-square" src="/deloitte.jpg" />
              <img className="aspect-square" src="/amex.jpg" />
              <img className="aspect-square" src="/mcgovern.jpg" />
              <img className="aspect-square" src="/zendesk.jpg" />
              <img className="aspect-square" src="/sunday.jpg" />
              <img className="aspect-square" src="/erb.jpg" />
              <img className="aspect-square" src="/dte.jpg" />
              <img className="aspect-square" src="/seedfund.jpg" />
              <img className="aspect-square" src="/summit.jpg" />
            </div>
          </div>
          <div className="h-40"></div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

About.getLayout = (page) => <Layout title={"About"}>{page}</Layout>
export default About
